import { useState } from 'react';
import { useApp } from '../store';
import Icon from './Icon';
import { alreadyLogged, type DecisionDraft } from '../decisions';

/**
 * ONE-CLICK "log this as a decision" — the derive-first affordance for the
 * surfaces where the decision is already made and written down: a settled
 * sign-off (carries `Signoff.why`) and a change-request assumption (carries the
 * business decision). The host builds the draft with the matching composer
 * (decisionFromSignoff / decisionFromChangeRequest) and hands it here.
 *
 * It writes through the store's `logDecision` (returns '' or an error), then
 * replaces itself with a quiet, non-nagging confirmation — it does NOT navigate
 * away, because the decision is reachable from the project's Decisions tab and
 * the person is mid-task on the sign-off or the CR.
 *
 * It is a QUIET secondary button, not a petrol primary: embedded in a list of
 * settled sign-offs or CR assumptions, a loud button would out-shout the one
 * live item that still needs attention (P7). The host decides WHEN to render it
 * and gates it to a writer — the decisions RLS is the backstop either way.
 */
export default function LogDecisionAction({
  draft,
  label,
  note,
}: {
  draft: DecisionDraft;
  label: string;
  /** A quiet aside beside the button — e.g. "Carries the sign-off reason". */
  note?: string;
}) {
  const logDecision = useApp((s) => s.logDecision);
  const decisions = useApp((s) => s.decisions);
  const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle');
  const [problem, setProblem] = useState('');

  // Already logged? The shared derive-first guard (alreadyLogged) reads the live
  // log for a decision from this same source with the same title, so the
  // confirmation survives a reload and a second click never logs a duplicate.
  const already = alreadyLogged(decisions, draft);

  if (state === 'done' || already) {
    return (
      <span className="seed-done">
        <Icon name="check" size={14} />
        In the log
      </span>
    );
  }

  return (
    <>
      <button
        className="btn btn-small"
        disabled={state === 'busy'}
        onClick={async () => {
          setState('busy');
          setProblem('');
          const err = await logDecision(draft);
          if (err) {
            setProblem(err);
            setState('idle');
            return;
          }
          setState('done');
        }}
      >
        {label}
      </button>
      {problem ? (
        <span className="seed-note" role="alert">
          {problem}
        </span>
      ) : (
        note && (
          <span className="seed-note">
            <Icon name="link" size={13} />
            {note}
          </span>
        )
      )}
    </>
  );
}
